import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Heart, LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { getErrorMessage } from "../lib/api";
import Panda from "../components/Panda";
import { CurveBottom, Cloud } from "../components/Decor";
import { useAuth } from "../context/AuthContext";
import { useProgress } from "../context/ProgressContext";

const AcceptInvite = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, loading } = useAuth();
  const { refreshFollowing, refreshChildren } = useProgress();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const redirect = `/invite/accept?token=${token}`;

  useEffect(() => {
    if (!token) { setError("This invitation link is missing its token."); return; }
    api.get(`/invites/lookup/${token}`).then((r) => setInfo(r.data)).catch((err) => setError(getErrorMessage(err, "This invitation link is not valid")));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const r = await api.post("/invites/accept", { token });
      if (r.data.role === "parent") {
        await refreshChildren();
        toast.success(`You're now a co-parent with ${r.data.owner_name}!`);
        navigate("/family");
        return;
      }
      await refreshFollowing();
      toast.success(`You're now following ${r.data.child_names.join(", ")}!`);
      navigate("/family");
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not accept invitation"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="accept-invite-page">
      <section className="relative bg-[#63c79a] overflow-hidden">
        <Cloud className="absolute left-[6%] top-8 hidden md:block cloud-drift" size={180} />
        <div className="relative max-w-5xl mx-auto px-6 pt-14 pb-28 text-center">
          <h1 className="text-white font-bold text-4xl sm:text-5xl" style={{ fontFamily: "Literata, serif" }}>You've been invited!</h1>
          <p className="text-[#0d3d2b] text-lg mt-4 max-w-xl mx-auto font-medium">Follow a child's credit-learning journey and cheer them on.</p>
        </div>
        <CurveBottom color="#ffffff" />
      </section>

      <section className="max-w-xl mx-auto px-6 py-10">
        <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
          {error ? (
            <>
              <p className="text-gray-700" data-testid="invite-error">{error}</p>
              <Link to="/" className="mt-5 inline-block btn-pill bg-[#1b8bc9] text-white">Go home</Link>
            </>
          ) : !info ? (
            <p className="text-gray-500">Loading invitation...</p>
          ) : (
            <>
              <div className="flex justify-center -space-x-6">
                {info.child_names.slice(0, 3).map((n, i) => <Panda key={`${n}-${i}`} size={90} accessory={["none", "glasses", "bowtie"][i]} scarf={["#e5356f", "#1b8bc9", "#63c79a"][i]} />)}
              </div>
              <Heart className="mx-auto text-[#e5356f] mt-2" fill="#e5356f" size={28} />
              <h2 className="text-2xl font-bold text-[#0c4a6e] mt-2" style={{ fontFamily: "Literata, serif" }} data-testid="invite-headline">
                {info.role === "parent" ? `${info.inviter_name} invited you to co-parent ${info.child_names.join(", ")}` : `${info.inviter_name} invited you to follow ${info.child_names.join(", ")}`}
              </h2>
              {info.role === "parent" && <p className="mt-2 text-xs font-bold uppercase tracking-widest text-[#7d5fb2]" data-testid="invite-role-badge">Full access - second parent</p>}
              {info.message && <p className="mt-3 text-gray-600 italic">"{info.message}"</p>}
              <p className="mt-3 text-sm text-gray-500">{info.role === "parent" ? "You'll share the whole family: add and edit children, run lessons, review worksheets and manage invites." : "You'll see lessons completed, quiz scores and certificates, and you can send cheers. Read-only - you can't change anything."}</p>

              {info.status !== "pending" ? (
                <p className="mt-6 font-bold text-[#e5356f]" data-testid="invite-status">This invitation is {info.status}.{info.status === "accepted" && <> <Link to="/family" className="underline">Open your dashboard</Link></>}</p>
              ) : loading ? (
                <p className="mt-6 text-gray-500">Checking your account...</p>
              ) : user && (user.id === info.inviter_id || user.id === info.invited_by) ? (
                <p className="mt-6 text-sm text-[#5a4600] bg-[#fff8e1] border border-[#f3c932] rounded-2xl p-4" data-testid="invite-own">
                  This is an invitation you sent. Share the link with <span className="font-bold">{info.email}</span> - they'll accept it from their own account.
                </p>
              ) : user ? (
                <button onClick={accept} disabled={busy} className="mt-6 btn-pill-lg bg-[#63c79a] hover:bg-[#4fb98a] text-white shadow-lg disabled:opacity-60" data-testid="accept-invite-button">{busy ? "Accepting..." : `Accept as ${user.name}`}</button>
              ) : (
                <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/login" state={{ tab: "register", redirect, email: info.email }} className="btn-pill-lg bg-[#e5356f] hover:bg-[#c92a5e] text-white shadow inline-flex items-center gap-2" data-testid="invite-signup"><UserPlus size={18} /> Create free account</Link>
                  <Link to="/login" state={{ tab: "login", redirect, email: info.email }} className="btn-pill-lg bg-white border-2 border-[#1b8bc9] text-[#1b8bc9] hover:bg-[#e9f9fd] inline-flex items-center gap-2" data-testid="invite-login"><LogIn size={18} /> Log in</Link>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default AcceptInvite;
